import styled, { keyframes } from 'styled-components'
import { useCallback, useMemo } from 'react'
import colors from '../utils/style/colors'
import { Link } from 'react-router-dom'

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`
const bgShift = keyframes`
  from { filter: brightness(1); }
  to   { filter: brightness(1.1) saturate(1.1); }
`
/* Le champ ondule : chaque tige porte sa propre amplitude (--a), sa
   durée et son décalage, sinon tout le champ respire d'un bloc. */
const souffle = keyframes`
  0%, 100% { transform: rotate(var(--a)); }
  50%      { transform: rotate(calc(var(--a) * -1)); }
`
const flotte = keyframes`
  0%   { transform: translate3d(0, 0, 0); opacity: 0; }
  12%  { opacity: var(--o); }
  88%  { opacity: var(--o); }
  100% { transform: translate3d(var(--dx), var(--dy), 0); opacity: 0; }
`
const lueur = keyframes`
  0%, 100% { opacity: 0.20; }
  50%      { opacity: 0.42; }
`
const scrollPulse = keyframes`
  0%, 100% { opacity: 0.4; transform: translateY(0); }
  50%       { opacity: 0.9; transform: translateY(7px); }
`

const Wrapper = styled.section`
  position: relative;
  height: 100vh;
  min-height: 620px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${colors.heroBg};
  @media (max-width: 768px) { min-height: 100svh; }
`
const AnimBg = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 60% at 20% 60%, rgba(58,90,46,0.55) 0%, transparent 65%),
    radial-gradient(ellipse 70% 50% at 80% 40%, rgba(107,76,42,0.4) 0%, transparent 60%),
    linear-gradient(160deg, #0e1a0a 0%, #1e2e14 40%, #2a1a0a 100%);
  animation: ${bgShift} 10s ease-in-out infinite alternate;
`
const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.06;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 200px 200px;
`
const Content = styled.div`
  position: relative;
  z-index: 10;
  text-align: center;
  padding: 0 24px;
  padding-bottom: 220px;
  max-width: 820px;
  animation: ${fadeUp} 1.2s 0.2s both;
`
const Badge = styled.span`
  display: inline-block;
  background: rgba(200,168,75,0.15);
  border: 1px solid rgba(200,168,75,0.4);
  color: ${colors.orClair};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 7px 18px;
  border-radius: 2px;
  margin-top: 100px;
  margin-bottom: 10px;
  font-family: ${colors.font};
`
const HeroTitle = styled.h1`
  font-family: 'Playfair Display';
  font-size: clamp(2.6rem, 6vw, 5rem);
  color: #fff;
  line-height: 1.08;
  margin-bottom: 22px;
  text-shadow: 0 4px 32px rgba(0,0,0,0.5);
  em { color: ${colors.orClair}; font-style: normal; }
  t1, t2 { color: ${colors.orClair}; }
  @media (max-width: 768px) { font-size: 2.2rem; }
`
const HeroSub = styled.p`
  font-size: 1.1rem;
  color: rgba(255,255,255,0.75);
  line-height: 1.68;
  margin: 0 auto 42px;
  font-weight: 300;
  max-width: 560px;
  font-family: ${colors.font};
  em { font-style: italic; color: ${colors.orClair}; }
  @media (max-width: 768px) { font-size: 0.95rem; }
`
const BtnRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`
const BtnPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${colors.or};
  color: ${colors.texte};
  padding: 16px 34px;
  border-radius: 3px;
  font-family: ${colors.font};
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  transition: all 0.25s;
  box-shadow: 0 4px 20px rgba(200,168,75,0.35);
  &:hover { background: ${colors.orClair}; transform: translateY(-2px); box-shadow: 0 8px 32px rgba(200,168,75,0.5); }
`
const BtnSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: #fff;
  padding: 16px 34px;
  border: 1.5px solid rgba(255,255,255,0.4);
  border-radius: 3px;
  font-family: ${colors.font};
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  transition: all 0.25s;
  &:hover { border-color: ${colors.orClair}; color: ${colors.orClair}; transform: translateY(-2px); }
`

/* ── SCÈNE — un champ de céréales en contre-jour ──
   La paille avant la botte et avant le camion. Rien de figuratif à
   reconnaître ici : le mouvement se règle par son amplitude, pas par
   la ressemblance. */
const Champ = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 190px;
  pointer-events: none;
  overflow: hidden;
  z-index: 1;

  /* Les liens « Suivre mon devis » et « Découvrir » vivent dans les coins
     bas : le champ s'y estompe pour ne pas les avaler. */
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 16%, #000 84%, transparent);
  mask-image: linear-gradient(90deg, transparent, #000 16%, #000 84%, transparent);

  /* Sur un écran étroit, 16 % ne suffisent pas : les deux liens
     mordent encore sur les épis. Le champ se resserre au centre. */
  @media (max-width: 768px) {
    height: 150px;
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 30%, #000 70%, transparent);
    mask-image: linear-gradient(90deg, transparent, #000 30%, #000 70%, transparent);
  }
`
const Couche = styled.svg`
  position: absolute;
  bottom: 0; left: 0;
  width: 100%;
  height: ${(p) => p.$h}px;
  opacity: ${(p) => p.$op};
  display: block;
`
const Tige = styled.g`
  transform-box: fill-box;
  transform-origin: bottom center;
  animation: ${souffle} var(--dur) ease-in-out var(--delai) infinite;
  will-change: transform;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`
/* Poussière de paille en suspension, comme dans un hangar au soleil. */
const Poudre = styled.span`
  position: absolute;
  border-radius: 50%;
  background: ${colors.orClair};
  animation: ${flotte} var(--dur) linear var(--delai) infinite;
  will-change: transform, opacity;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: var(--o);
  }
`
const Poussiere = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 1;
`
const Halo = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 190px;
  pointer-events: none;
  background: radial-gradient(60% 100% at 50% 100%, rgba(200,168,75,0.16), transparent 70%);
  animation: ${lueur} 7s ease-in-out infinite;
  z-index: 1;
  @media (prefers-reduced-motion: reduce) { animation: none; opacity: 0.3; }
`
const Sol = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 26px;
  background: linear-gradient(180deg, rgba(10,16,7,0), rgba(8,13,5,0.9) 70%);
  pointer-events: none;
  z-index: 2;
`

const alea = (a, b) => a + Math.random() * (b - a)

/* Une tige : une hampe courbe, un épi, et ses barbes. Sans les barbes
   la silhouette fait roseau et non céréale. */
function dessinerTige(x, h, sens) {
  const cintre = alea(3, 9) * sens
  const yEpi = 100 - h
  const hEpi = h * alea(0.26, 0.36)
  const larg = hEpi * alea(0.10, 0.15)
  const cx = x + cintre * 1.5
  const barbes = []
  for (let i = 0; i < 6; i += 1) {
    const by = yEpi + hEpi * (i / 5)
    const bl = hEpi * alea(0.35, 0.6)
    barbes.push(`M${cx} ${by} L${cx - larg - bl * 0.22} ${by - bl * 1.35}`)
    barbes.push(`M${cx} ${by} L${cx + larg + bl * 0.22} ${by - bl * 1.35}`)
  }
  return {
    hampe: `M${x} 100 Q ${x + cintre} ${100 - h * 0.55} ${cx} ${yEpi}`,
    barbes: barbes.join(' '),
    cx, cyEpi: yEpi + hEpi / 2, rx: larg, ry: hEpi / 2,
    amp: alea(1.1, 2.6),
    dur: alea(3.4, 6.2),
    delai: alea(-6, 0),
  }
}

/* Trois plans : le plus lointain est le plus sombre et le plus lent. */
const PLANS = [
  { n: 54, h: 92,  op: 0.26, c: '#5d4f24', w: 0.8,  min: 44, max: 66, amp: 0.60 },
  { n: 42, h: 118, op: 0.50, c: '#8a7238', w: 1.05, min: 56, max: 82, amp: 0.85 },
  { n: 30, h: 146, op: 0.82, c: '#c8a84b', w: 1.3,  min: 68, max: 96, amp: 1.15 },
]


const ScrollHint = styled.button`
  text-shadow: 0 1px 3px rgba(8,13,5,0.95), 0 2px 12px rgba(8,13,5,0.9);
  position: absolute;
  bottom: 80px;
  right: 48px;
  transform: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: rgba(255,255,255,0.7);
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-family: ${colors.font};
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  padding: 12px 20px;
  animation: ${scrollPulse} 2s ease-in-out infinite;
  z-index: 10;
  transition: color 0.2s;
  &:hover { color: ${colors.orClair}; }
  &:hover div { background: ${colors.orClair}; }
  @media (max-width: 768px) { bottom: 72px; right: 2px; padding: 12px 10px; font-size: 0.62rem; letter-spacing: 0.12em; }
`
const ScrollDot = styled.div`
  width: 8px; height: 8px;
  background: ${colors.or};
  border-radius: 50%;
  transition: background 0.2s;
  box-shadow: 0 0 8px rgba(200,168,75,0.5);
`
const ScrollChevron = styled.div`
  width: 18px; height: 18px;
  border-right: 2px solid ${colors.or};
  border-bottom: 2px solid ${colors.or};
  transform: rotate(45deg);
  margin-top: -4px;
  transition: border-color 0.2s;
`

const SuiviHint = styled(Link)`
  text-shadow: 0 1px 3px rgba(8,13,5,0.95), 0 2px 12px rgba(8,13,5,0.9);
  position: absolute;
  bottom: 80px;
  left: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: rgba(255,255,255,0.7);
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-family: ${colors.font};
  font-weight: 600;
  text-decoration: none;
  padding: 12px 20px;
  animation: ${scrollPulse} 2s ease-in-out infinite;
  animation-delay: 1s;
  z-index: 10;
  transition: color 0.2s;
  &:hover { color: ${colors.orClair}; }
  &:hover div { background: ${colors.orClair}; border-color: ${colors.orClair}; }

  /* Masqué jusqu'ici faute de place à côté du camion. Le camion parti,
     le lien reprend sa place — resserré contre le bord. */
  @media (max-width: 768px) {
    left: 2px;
    bottom: 72px;
    padding: 12px 10px;
    font-size: 0.62rem;
    letter-spacing: 0.12em;
  }
`
const ScrollChevronUp = styled.div`
  width: 18px; height: 18px;
  border-left: 2px solid ${colors.or};
  border-top: 2px solid ${colors.or};
  transform: rotate(45deg);
  margin-bottom: -4px;
  transition: border-color 0.2s;
`
const ScrollDotSmall = styled.div`
  width: 8px; height: 8px;
  background: ${colors.or};
  border-radius: 50%;
  transition: background 0.2s;
  box-shadow: 0 0 8px rgba(200,168,75,0.5);
`

function Hero() {
  const scrollToProduits = useCallback(() => {
    const el = document.getElementById('produits')
    if (!el) return
    const target = el.getBoundingClientRect().top + window.scrollY
    const start = window.scrollY
    const distance = target - start
    const duration = 2200
    let startTime = null
    const easeInOutCubic = (t) => t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2,3)/2
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp
      const elapsed = timestamp - startTime
      const progress = Math.min(elapsed / duration, 1)
      window.scrollTo(0, start + distance * easeInOutCubic(progress))
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [])

  /* Généré une fois : reproduire le champ à chaque rendu le ferait
     sauter d'une frame à l'autre. */
  const plans = useMemo(() => PLANS.map((p) => (
    Array.from({ length: p.n }, (_, i) => {
      const t = dessinerTige(
        (i + alea(-0.35, 0.35)) * (1000 / p.n),
        alea(p.min, p.max),
        Math.random() < 0.5 ? -1 : 1,
      )
      return { ...t, amp: (t.amp * p.amp).toFixed(2) }
    })
  )), [])

  const grains = useMemo(() => Array.from({ length: 40 }, () => ({
    taille: `${alea(1.2, 3.4).toFixed(1)}px`,
    left: `${alea(0, 100).toFixed(1)}%`,
    bottom: `${alea(-4, 40).toFixed(1)}%`,
    o: alea(0.18, 0.62).toFixed(2),
    dx: `${alea(-60, 90).toFixed(0)}px`,
    dy: `${alea(-150, -320).toFixed(0)}px`,
    dur: `${alea(11, 26).toFixed(1)}s`,
    delai: `${(-alea(0, 22)).toFixed(1)}s`,
  })), [])

  return (
    <Wrapper>
      <AnimBg />
      <Grain />
      {/*
        💡 Vidéo IA — décommentez quand votre mp4 est prêt
        <video style={{position:'absolute',inset:0,width:'100%',height:'100%',
          objectFit:'cover',opacity:0.45}} autoPlay muted loop playsInline>
          <source src="/videos/hero.mp4" type="video/mp4" />
        </video>
      */}

      <Content>
        <Badge>🇫🇷 Export direct France → Suisse 🇨🇭</Badge>
        <HeroTitle>
        <t1>PAILLE </t1><em>&amp;</em> <t2>FOURRAGE,</t2><br />LIVRÉS EN SUISSE
        </HeroTitle>
        <HeroSub>
          Qualité - Simplicité - Confiance
          <em>
            <br />✔️&nbsp; Livraison directe en Suisse
            <br />✔️&nbsp; Pour éleveurs, centres équestres, agriculteurs
            <br />✔️&nbsp; Produits de qualité, sélectionnés, avec ticket de pesée
          </em>
        </HeroSub>
        <BtnRow>
          <BtnPrimary to="/devis">
            <i className="fa-solid fa-truck" />
            Demander un devis gratuit
          </BtnPrimary>
          <BtnSecondary to="/produits">
            <i className="fa-solid fa-seedling" />
            Voir nos produits
          </BtnSecondary>
        </BtnRow>
      </Content>

      <Poussiere aria-hidden="true">
        {grains.map((g, i) => (
          <Poudre
            key={i}
            style={{
              width: g.taille, height: g.taille, left: g.left, bottom: g.bottom,
              '--o': g.o, '--dx': g.dx, '--dy': g.dy, '--dur': g.dur, '--delai': g.delai,
            }}
          />
        ))}
      </Poussiere>

      <Halo aria-hidden="true" />

      <Champ aria-hidden="true">
        {plans.map((plan, i) => (
          <Couche
            key={i} $h={PLANS[i].h} $op={PLANS[i].op}
            viewBox="0 0 1000 100" preserveAspectRatio="none"
          >
            {plan.map((t, j) => (
              <Tige
                key={j}
                style={{ '--a': `${t.amp}deg`, '--dur': `${t.dur}s`, '--delai': `${t.delai}s` }}
              >
                <g fill="none" stroke={PLANS[i].c} strokeWidth={PLANS[i].w} strokeLinecap="round">
                  <path d={t.hampe} />
                  <path d={t.barbes} />
                </g>
                <ellipse cx={t.cx} cy={t.cyEpi} rx={t.rx} ry={t.ry} fill={PLANS[i].c} />
              </Tige>
            ))}
          </Couche>
        ))}
        <Sol />
      </Champ>

      <ScrollHint onClick={scrollToProduits} aria-label="Découvrir nos produits">
        <ScrollDot />
        Découvrir
        <ScrollChevron />
      </ScrollHint>

      <SuiviHint to="/suivi-devis">
        <ScrollChevronUp />
        Suivre mon devis
        <ScrollDotSmall />
      </SuiviHint>
    </Wrapper>
  )
}

export default Hero
